/**
 * LOS NEGOCIOS QUE YA NO TRABAJAN CON NEXO SE ARCHIVAN, Y LAS LISTAS MUESTRAN LOS QUE SI (NEGOCIOS-ARCHIVAR-VISTA-T1,
 * Josse 6-oct via Ares #22467).
 *
 * DE DONDE SALE. Josse, con capturas: en Negocios y en Nexo Desktop "que solo nos aparezcan los activos", "la unica
 * opcion es eliminarlos" y "verificar por que aparecen 52 instalaciones activas". Medido 6-oct: la lista mostraba
 * todos (Taller y Almacen La Guacca, inactivo, incluido) y Nexo Desktop contaba FILAS de license_machines (52) cuando
 * eran 26 PCs (Fantasia Tropical 12 filas del mismo PC).
 *
 * LAS REGLAS (contrato de A, NEGOCIOS-ARCHIVAR-API-T1 86128dc y LIC-UN-PC-POR-NEGOCIO-T1 2540a1b):
 *   1. "trabajan con Nexo" = status active o pending; archived, inactive y suspended solo con filtro.
 *   2. el panel pide /all?status=all y filtra el mismo: sirve con la API de antes (que no filtra) y con la nueva
 *      (que sin status ya no trae los archivados).
 *   3. un PC es un nombre de PC distinto (lower(trim(pc_name))) por negocio, como v_negocios_equipos; se excede si
 *      pasan de los permitidos (1, o lo que diga la excepcion declarada cuando la API la publique).
 *   4. archivar pide motivo; si la API responde 409 ARCHIVAR_SUSCRIPCION_VIGENTE se pide confirmar y se reenvia.
 *
 * Sin DOM: lo prueba scripts/check-negocios-archivo.mjs.
 */

import { idDelNegocio } from './idDelNegocio.js';

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

export const TRABAJAN = ['active', 'pending'];
export const ARCHIVADOS = ['archived', 'inactive', 'suspended'];

export const VISTAS = {
  activos: { etiqueta: 'Activos', estados: TRABAJAN },
  archivados: { etiqueta: 'Archivados e inactivos', estados: ARCHIVADOS },
  todos: { etiqueta: 'Todos', estados: null }
};

// El enum BusinessStatus de la API en numero (si algun dia viaja sin el convertidor a texto): Active, Inactive,
// Suspended, Pending, Archived, en ese orden (86128dc).
const POR_NUMERO = ['active', 'inactive', 'suspended', 'pending', 'archived'];

/** El estado del negocio en minusculas: el API manda el enum ("Active", "Archived") y la base "active". */
export function estadoDe(b) {
  const s = texto(b?.status ?? b?.Status).toLowerCase();
  return /^\d$/.test(s) ? (POR_NUMERO[Number(s)] || s) : s;
}

/** La lista que llega del API, venga suelta o en su sobre. */
export function listaDe(res) {
  const d = res && typeof res === 'object' && 'ok' in res ? res.data : res;
  const adentro = d?.data ?? d;
  const items = adentro?.items ?? adentro;
  return Array.isArray(items) ? items : [];
}

/** Los negocios de una vista (activos por defecto). Un negocio sin estado cuenta como activo: es lo que era antes. */
export function filtrarPorVista(items, vista = 'activos') {
  const v = VISTAS[vista] || VISTAS.activos;
  const lista = Array.isArray(items) ? items : [];
  if (!v.estados) return lista.slice();
  return lista.filter((b) => v.estados.includes(estadoDe(b) || 'active'));
}

/** Cuantos hay en cada vista, para los botones del filtro. */
export function conteoPorVista(items) {
  return Object.fromEntries(Object.keys(VISTAS).map((k) => [k, filtrarPorVista(items, k).length]));
}

const ETIQUETAS = {
  active: ['activo', '#238636'], pending: ['pendiente', '#d29922'], archived: ['archivado', '#6e7681'],
  inactive: ['inactivo', '#6e7681'], suspended: ['suspendido', '#f85149']
};

/** {etiqueta, color} del estado del negocio. */
export function insigniaDeEstado(b) {
  const [etiqueta, color] = ETIQUETAS[estadoDe(b) || 'active'] || [estadoDe(b) || 'sin estado', '#6e7681'];
  return { etiqueta, color };
}

export const estaArchivado = (b) => ARCHIVADOS.includes(estadoDe(b));

/** El nombre del PC como lo cuenta v_negocios_equipos. */
const pcDe = (m) => texto(m?.pc_name ?? m?.pcName).toLowerCase();

/**
 * PCs distintos por negocio. machines = filas activas de /api/license/machines; equipos = filas de la API de
 * v_negocios_equipos si existe (pcsPermitidos, excede); si no, se permite 1.
 * -> Map(businessId -> {pcs, filas, nombres[], permitidos, excede, conExcepcion})
 */
export function equiposPorNegocio(machines, equipos = []) {
  const permitidosDe = new Map();
  for (const e of Array.isArray(equipos) ? equipos : []) {
    const id = idDelNegocio(e);
    const p = Number(e?.pcsPermitidos ?? e?.pcs_permitidos);
    if (id && p > 0) permitidosDe.set(id, p);
  }
  const porNegocio = new Map();
  for (const m of Array.isArray(machines) ? machines : []) {
    if (m?.active === false) continue;
    const id = texto(m?.business_id ?? m?.businessId);
    if (!id) continue;
    const r = porNegocio.get(id) || { filas: 0, nombres: new Set() };
    r.filas += 1;
    const pc = pcDe(m);
    if (pc) r.nombres.add(pc);
    porNegocio.set(id, r);
  }
  const salida = new Map();
  for (const [id, r] of porNegocio) {
    const pcs = r.nombres.size || (r.filas ? 1 : 0);
    const permitidos = permitidosDe.get(id) ?? 1;
    salida.set(id, { pcs, filas: r.filas, nombres: [...r.nombres], permitidos, excede: pcs > permitidos, conExcepcion: permitidos > 1 });
  }
  return salida;
}

/** Totales para la tarjeta: PCs distintos y filas, solo de los negocios dados. */
export function totalesDeEquipos(equipos, idsVisibles) {
  let pcs = 0, filas = 0, exceden = 0;
  for (const [id, e] of equipos) {
    if (idsVisibles && !idsVisibles.has(id)) continue;
    pcs += e.pcs; filas += e.filas; if (e.excede) exceden += 1;
  }
  return { pcs, filas, exceden };
}

/** El cuerpo de archivar, o el error que lo frena. */
export function cuerpoDeArchivar(motivo, confirmarSuscripcionVigente = false) {
  const m = texto(motivo);
  if (m.length < 3) return { ok: false, error: 'Escribe el motivo (por ejemplo: ya no trabaja con Nexo)' };
  return { ok: true, cuerpo: { motivo: m, confirmarSuscripcionVigente: !!confirmarSuscripcionVigente } };
}

/**
 * Que hacer con la respuesta de archivar / desarchivar / eliminar.
 * -> {hecho} | {pideConfirmar, mensaje} | {sinPublicar, mensaje} | {conHistoria, mensaje} | {error}
 */
export function leerRespuestaDeArchivo(res) {
  if (res?.ok) return { hecho: true };
  const status = Number(res?.code);
  const det = res?.details && typeof res.details === 'object' ? res.details : {};
  const code = texto(det.code);
  const delCuerpo = texto(det.error || det.message);
  // La base contesta "CODIGO: frase"; a la persona se le dice la frase.
  const mensaje = (delCuerpo || texto(res?.error)).replace(/^[A-Z][A-Z_]+:\s*/, '');
  if (code === 'ARCHIVAR_SUSCRIPCION_VIGENTE') return { pideConfirmar: true, mensaje: mensaje || 'El negocio tiene la suscripcion vigente.' };
  if (code === 'NEGOCIO_CON_HISTORIA') return { conHistoria: true, mensaje: mensaje || 'Este negocio tiene ventas, equipos o pagos: archivalo en vez de borrarlo.' };
  // Ruta que no existe: 404 SIN cuerpo (ApiBase igual pone un texto generico en res.error; por eso se mira el cuerpo).
  // "Empresa no encontrada" trae cuerpo y es otro caso.
  if (status === 404 && !code && !delCuerpo) return { sinPublicar: true, mensaje: 'Archivar aun no esta publicado en la API.' };
  if (status === 405) return { sinPublicar: true, mensaje: 'Archivar aun no esta publicado en la API.' };
  return { error: mensaje || 'No se pudo completar' };
}
